<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <b-col
        lg="12"
        class="d-flex align-items-center justify-content-center auth-bg px-2 p-lg-5"
      >
        <b-card>
          <b-card-title title-tag="h2" class="font-weight-bold mb-1">
            {{ loading ? 'Signing you in...' : 'Processing...' }}
          </b-card-title>
          <b-card-text class="mb-2">
            {{ loading ? 'Please wait while we complete your sign-in.' : 'Please wait.' }}
          </b-card-text>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from 'axios'
import jwt from 'jsonwebtoken'
import { mapActions } from 'vuex'
import { BRow, BCol, BCard, BCardText, BCardTitle } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

// Constants
const SSO_TOKEN_ENDPOINT = '/auth/sso/tubtim/token'
const QUERY_PARAM_TEMP_TOKEN = 't'
const QUERY_PARAM_ERROR = 'error'
const HTTP_TIMEOUT_MS = 30000

export default {
  name: 'TubtimSSOCallback',
  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BCardTitle
  },
  data () {
    return {
      loading: true
    }
  },
  methods: {
    ...mapActions({
      setUser: 'User/setUser'
    }),
    /**
     * Clean query parameters from URL to prevent token/error leakage
     */
    cleanUrlParams () {
      try {
        window.history.replaceState(null, document.title, window.location.pathname)
      } catch (error) {
        // Ignore history API errors
        console.warn('[SSO] Failed to clean URL params:', error)
      }
    },
    /**
     * Show error toast notification
     * @param {string} message - Error message
     */
    showErrorToast (message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'SSO Login Failed',
          text: message || 'An error occurred during sign-in',
          icon: 'AlertCircleIcon',
          variant: 'danger'
        }
      })
    },
    /**
     * Show success toast notification
     */
    showSuccessToast () {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'SSO Login Success',
          text: 'You have been successfully signed in',
          icon: 'CheckIcon',
          variant: 'success'
        }
      })
    },
    /**
     * Validate and extract user data from JWT token
     * @param {string} token - JWT token
     * @returns {Object} Decoded user data
     * @throws {Error} If token is invalid
     */
    validateAndDecodeToken (token) {
      if (!token || typeof token !== 'string') {
        throw new Error('Invalid token format')
      }

      const decoded = jwt.decode(token)
      if (!decoded || !decoded.id) {
        throw new Error('Invalid token: missing user information')
      }

      return decoded
    },
    /**
     * Exchange temporary token for BRS JWT
     * @param {string} tempToken - Temporary token from query parameter
     * @returns {Promise<{ token: string, nexusSeat: boolean }>}
     */
    async exchangeToken (tempToken) {
      const apiUrl = process.env.VUE_APP_API_URL || ''
      if (!apiUrl) {
        throw new Error('API URL is not configured')
      }

      const response = await axios.get(`${apiUrl}${SSO_TOKEN_ENDPOINT}`, {
        params: { [QUERY_PARAM_TEMP_TOKEN]: tempToken },
        timeout: HTTP_TIMEOUT_MS
      })

      if (!response?.data?.success || !response?.data?.token) {
        throw new Error(response?.data?.message || 'Failed to exchange temporary token')
      }

      return {
        token: response.data.token,
        nexusSeat: Boolean(response.data.nexusSeat)
      }
    },
    /**
     * Handle SSO callback - exchange token and set user session
     */
    async handleSSOCallback () {
      try {
        this.loading = true

        // Check for error in query parameter
        const error = this.$route.query[QUERY_PARAM_ERROR]
        if (error) {
          throw new Error(decodeURIComponent(error))
        }

        // Get temporary token from query parameter
        const tempToken = this.$route.query[QUERY_PARAM_TEMP_TOKEN]
        if (!tempToken || typeof tempToken !== 'string') {
          throw new Error('Missing or invalid temporary token')
        }

        // Exchange temporary token for BRS JWT
        const { token, nexusSeat } = await this.exchangeToken(tempToken)

        // Validate and decode token
        const decoded = this.validateAndDecodeToken(token)

        // Set user in Vuex store
        this.setUser({
          id: decoded.id,
          username: decoded.username,
          firstName: decoded.firstName,
          lastName: decoded.lastName,
          email: decoded.email,
          role: decoded.role,
          userGroupId: decoded.userGroupId,
          userTeamId: decoded.userTeamId,
          allowedGroupIds: decoded.allowedGroupIds || [],
          allowedTeamIds: decoded.allowedTeamIds || [],
          zoomEnabled: Boolean(decoded.zoomEnabled),
          token,
          isTubtimSso: nexusSeat
        })

        // Clean URL and show success message
        this.cleanUrlParams()
        this.showSuccessToast()

        // Redirect to dashboard
        await this.$nextTick()
        this.$router.replace('/')
      } catch (error) {
        // Log error for debugging (only in development)
        if (process.env.NODE_ENV !== 'production') {
          console.error('[SSO Callback Error]', error)
        }

        // Show error toast
        const errorMessage = error?.response?.data?.message || error?.message || 'An unexpected error occurred'
        this.showErrorToast(errorMessage)

        // Clean URL and redirect to login
        this.cleanUrlParams()
        await this.$nextTick()
        this.$router.replace({ name: 'login' })
      } finally {
        this.loading = false
      }
    }
  },
  mounted () {
    // Start SSO callback process immediately
    this.handleSSOCallback()
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>

